import React, { memo, useCallback } from 'react'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'

import styles from './RequisitionCard.module.sass'
import ReactMarkdown from 'react-markdown'
import breaks from 'remark-breaks'
import SlidingText from '@jimmyboys/liita-ui/dist/components/SlidingText'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import RequisitionCardActions from './RequisitionCardActions/RequisitionCardActions'
import { useDrawerContext } from 'context/Drawer/DrawerContext'

const RequisitionCard = memo(
	(props) => {
		const { className, data, ...attributes } = props
		const labels = data.$cardLabels || {}
		const { openFile, i18n } = useGlobal()
		const { dispatch } = useDrawerContext()
		let summaryMedicalActs, summaryExams, summaryComments

		const DEFAULT_HISTORY = [
			{ observations: i18n`There are no additional changes` },
		]

		if (data.$detailLevel === 'summary') {
			summaryExams = data.exams
				? [
						labels.exams,
						...data.exams.map(
							(exam) =>
								`${
									exam.kValue
										? exam.rejected
											? '✗ '
											: '✓ ' + exam.refuse
											? '✗ '
											: '✓ '
										: ''
								} ${
									exam.requestedQuantity
										? exam.executedQuantity +
										  '/' +
										  exam.approvedQuantity +
										  '(' +
										  exam.requestedQuantity +
										  ')'
										: ''
								} - ${exam.code} - ${exam.name}`
						),
				  ]
				: []

			summaryMedicalActs = data.medicalActs
				? [
						labels.medicalActs,
						...data.medicalActs.map(
							(act) =>
								`${
									act.kValue
										? act.percentage + '% K: ' + act.kValue
										: ''
								} ${
									act.requestedQuantity
										? act.executedQuantity +
										  '/' +
										  act.approvedQuantity +
										  '(' +
										  act.requestedQuantity +
										  ')'
										: ''
								} - ${act.code} - ${act.name}`
						),
				  ]
				: []

			summaryComments = data.comments
				? [
						` ${data.comments.length} ${
							data.comments.length === 1
								? i18n`comment`
								: i18n`comments`
						}`,
				  ]
				: []
		}

		const clickItem = useCallback(
			(data) => (event) => {
				event.preventDefault()

				dispatch({
					action: 'OPEN_DRAWER',
					payload: {
						data: { processId: data.processId, id: data.id },
						type: 'CommentThreadDrawer',
					},
				})
			},
			[dispatch]
		)

		return (
			<div
				{...attributes}
				className={`card ${styles['root'] || ''} ${className || ''} ${
					data.$indented ? styles['indented'] : ''
				} ${
					data.$detailLevel === 'summary'
						? styles['summary-view']
						: ''
				}`}
				onClick={() => {
					data.$action && data.$action()
				}}
			>
				<Tooltip text={data.status?.description || ''}>
					<div className={styles['icon-container']}>
						<Icon
							className={styles['status-icon']}
							width='18px'
							height='18px'
							name={data?.status?.icon}
						/>
					</div>
				</Tooltip>
				<span className={styles['status-circle']} />
				<div
					className={`${styles['status-container']} ${
						styles[data?.status?.value?.toLowerCase()] || ''
					} ${data?.$highlight ? styles['highlighted'] : ''}`}
				>
					<div className={styles['status-content']}>
						<div>
							<div>
								<h5 className={styles['status-highlight']}>
									#
									<FuseHighlight
										matchedWords={
											data.$textHighlight
												?.matchedIndexes?.['id']
										}
										text={data.id}
									/>
									&nbsp;
									{` | ${
										data.scheduledDate?.shortDateFormat ||
										data.requisitionDate?.shortDateFormat
									} | ${data.type?.name}`}
								</h5>
							</div>
							<div>
								<span>
									<Icon
										className={styles['text-icon']}
										name='executed'
										width='14px'
										height='14px'
									/>
									<small>
										{data.scheduledDate?.shortTimeFormat ||
											data.requisitionDate
												?.shortTimeFormat}
										h
									</small>
								</span>
								{data?.establishment ? (
									<span>
										<Icon
											className={styles['text-icon']}
											name='location'
											width='14px'
											height='14px'
										/>
										<small>
											{data.establishment?.name}
										</small>
									</span>
								) : null}
							</div>
						</div>
					</div>
					{data.$detailLevel !== 'summary' && (
						<RequisitionCardActions data={data} />
					)}
					{data.$detailLevel === 'summary' && (
						<>
							<div className={styles['summary']}>
								<small className={styles['summary-content']}>
									{[
										...summaryExams,
										...summaryMedicalActs,
										...summaryComments,
									].join(';')}
								</small>
							</div>
							<div className={styles['summary-actions']}>
								<RequisitionCardActions data={data} />
							</div>
						</>
					)}
				</div>
				{data.$detailLevel !== 'summary' && (
					<div className={styles['details-container']}>
						{data.hasDocuments && (
							<ModalOptions
								title={labels.documents}
								options={data.documentsOptions}
								centerArrow
								fixedHeight='auto'
								alwaysAbove
								selectable={false}
								onSelect={(e) => {
									openFile && openFile(e)
									return true
								}}
							>
								<button className={styles['attachments']}>
									<Icon
										width='20px'
										height='20px'
										name='attachment'
									/>
								</button>
							</ModalOptions>
						)}
						<h5 className={styles['medical-acts-label']}>
							{labels.medicalActs}
						</h5>
						<div className={styles['inner-table']}>
							{data.medicalActs &&
								data.medicalActs.map((act, index) => (
									<SlidingText
										className={`${styles['table-text']} ${
											act.refused
												? styles[`refused-act`]
												: ''
										}`}
										key={index}
									>
										<small>
											{act.kValue ? (
												<>
													{act.percentage}% (K:{' '}
													{act.kValue})
												</>
											) : null}
											{act.requestedQuantity ? (
												<>
													{act.executedQuantity}/
													{act.approvedQuantity} (
													{act.requestedQuantity})
												</>
											) : null}{' '}
											<strong>{act.code}</strong>{' '}
											{act.name}
										</small>
									</SlidingText>
								))}
						</div>
						{data.text && (
							<>
								<h5 className={styles['observation-title']}>
									{labels.clinicalInformation}
								</h5>
								<div
									dynamic='true'
									className={
										styles['clinical-information-body']
									}
								>
									<div
										style={{
											minHeight:
												!isNaN(data.textHeight) &&
												data.textHeight > 0
													? data.textHeight
													: undefined,
											maxHeight:
												!isNaN(data.textHeight) &&
												data.textHeight > 0
													? data.textHeight
													: undefined,
										}}
									>
										{data.text && (
											<ReactMarkdown
												plugins={[breaks]}
												allowedTypes={[
													'break',
													'paragraph',
													'strong',
													'text',
													'list',
													'listItem',
												]}
												unwrapDisallowed={true}
											>
												{data.text}
											</ReactMarkdown>
										)}
									</div>
								</div>
							</>
						)}
						<span className={styles['request-info']}>
							<SlidingText>
								<small>
									{`${labels.requestedBy}: `}
									<strong>{data.applicant?.name}</strong>{' '}
									{` ${labels.toTheDateOf} `}
									<strong>
										{data.requisitionDate?.dateTimeFormat}
									</strong>
								</small>
							</SlidingText>
						</span>
						{data.exams && (
							<div>
								<h5 className={styles['exams-label']}>
									{labels.exams}
								</h5>
								<div className={styles['inner-table']}>
									{data.exams.map((act, index) => (
										<SlidingText
											className={styles['table-text']}
											key={index}
										>
											<small>
												{act.kValue ? (
													<small>
														{act.rejected
															? '✗'
															: '✓'}{' '}
														(
														{act.refuse ? '✗' : '✓'}
														)
													</small>
												) : null}
												{act.requestedQuantity ? (
													<small>
														{act.executedQuantity}/
														{act.approvedQuantity} (
														{act.requestedQuantity})
													</small>
												) : null}{' '}
												<strong>{act.code}</strong>{' '}
												{act.name}
											</small>
										</SlidingText>
									))}
								</div>
							</div>
						)}
						{data.comments && (
							<div>
								<h5 className={styles['exams-label']}>
									{labels.comments}
								</h5>
								<div className={styles['inner-table']}>
									{data.comments.map((comment, index) => (
										<SlidingText
											onClick={clickItem({
												id: comment.id,
												processId: comment.processId,
											})}
											className={`${styles['table-text']} ${styles['--clickable']}`}
											key={index}
										>
											<small>
												<strong>#{comment.id}</strong>{' '}
												{comment.body}
											</small>
										</SlidingText>
									))}
								</div>
							</div>
						)}
						{data.$detailLevel !== 'detailed' && (
							<div className={styles['history-content']}>
								<span>
									<Icon
										className={styles['text-icon']}
										name='executed'
										width='16px'
										height='16px'
									/>
									<small className={styles['history-title']}>
										<strong>{labels.changesHistory}</strong>
									</small>
								</span>
								<div>
									{(data.history && data.history.length
										? data.history
										: DEFAULT_HISTORY
									).map((elem, idx) => {
										return (
											<div
												key={idx}
												className={
													styles['history-line']
												}
											>
												<span
													className={
														styles['history-circle']
													}
												/>
												<SlidingText
													className={
														styles['history-text']
													}
												>
													<small>
														{
															elem
																?.modificationDate
																?.dateTimeFormat
														}{' '}
														<strong>
															{
																elem?.modificationUser
															}
														</strong>{' '}
														{elem?.observations}
													</small>
												</SlidingText>
											</div>
										)
									})}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		)
	},
	(prevProps, nextProps) =>
		prevProps.data.id === nextProps.data.id &&
		prevProps.data.$layout === nextProps.data.$layout &&
		prevProps.data.$detailLevel === nextProps.data.$detailLevel &&
		prevProps.data.$highlight === nextProps.data.$highlight &&
		prevProps.data.textHeight === nextProps.data.textHeight &&
		prevProps.style?.top === nextProps.style?.top &&
		prevProps.data?.$textHighlight?.query ===
			nextProps.data?.$textHighlight?.query
)

RequisitionCard.displayName = 'RequisitionCard'
RequisitionCard.defaultHeight = 118
RequisitionCard.defaultSummaryHeight = 50
RequisitionCard.skeletonDefaultHeight = 130
RequisitionCard.historyRowSize = 25
RequisitionCard.defaultRowSize = 20
RequisitionCard.defaultRows = 0
RequisitionCard.defaultLines = 0
export default RequisitionCard

export const RequisitionCardSkeleton = (props) => {
	const { className, data, ...attributes } = props

	return (
		<div
			{...attributes}
			className={`${styles['row-skeleton']} ${className || ''} ${
				styles[`${data?.$layout}-layout`] || ''
			}`}
		>
			<span className={styles['status-circle']} />
			<div
				className={`${styles['status-container-skeleton']} ${
					styles[data?.status?.value?.toLowerCase()] || ''
				}`}
			>
				<div className={styles['skeleton-status-content']}>
					<div>
						<h5>
							<Skeleton
								className={`${styles['skeleton']} ${styles['medium']}`}
							/>
						</h5>
						<small>
							<Skeleton className={styles['skeleton']} />
						</small>
					</div>
				</div>
			</div>
			<div className={styles['details-container-skeleton']}>
				<div className={`${styles['table']} ${styles['content']}`}>
					<span className={`${styles['cell']}`}>
						<div className={styles['inner-table-skeleton']}>
							<span className={`${styles['cell']}`}>
								<div>
									<small>
										<Skeleton
											className={`${styles['skeleton']} ${styles['small']}`}
										/>
									</small>
								</div>
							</span>
							<span className={`${styles['cell']}`}>
								<div>
									<small>
										<Skeleton
											className={`${styles['skeleton']} ${styles['small']}`}
										/>
									</small>
								</div>
							</span>
							<span className={`${styles['cell']}`}>
								<div>
									<small>
										<Skeleton
											className={`${styles['skeleton']} ${styles['small']}`}
										/>
									</small>
								</div>
							</span>
							<div>
								<h5>
									<Skeleton
										className={`${styles['skeleton']} ${styles['smaller']}`}
									/>
								</h5>
								<small>
									<Skeleton
										className={`${styles['skeleton']} ${styles['medium']}`}
									/>
								</small>
							</div>
						</div>
					</span>
				</div>
			</div>
		</div>
	)
}
